import { useEffect, useId, useRef, type ReactNode } from "react";

import { Button } from "./Button";
import { IconButton } from "./Button";
import { CloseIcon } from "./icons";

/**
 * A modal that behaves: focus moves in on open, Escape and backdrop clicks
 * close it, and focus returns to whatever opened it. On narrow viewports the
 * CSS renders it as a bottom sheet, matching the mobile apps' idiom.
 *
 * Focus is held by `aria-modal`, an initial focus into the first field and a
 * Tab trap: the dialog renders inline in the screen, so without one Tab walks
 * out of the last button onto the footer links under the backdrop.
 */
export function Modal({
  title,
  onClose,
  children,
  actions,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  // Held in a ref so the effect below can have an EMPTY dependency array.
  // Callers pass `onClose` as an inline arrow, so its identity changes on every
  // parent render — and in a chat thread the parent re-renders every time a
  // Firestore snapshot arrives. Keying the effect on it would tear down and
  // re-run setup mid-typing, snatching focus out of the field the person is
  // filling in and back to the dialog.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    openerRef.current = document.activeElement;
    // Every dialog in the product is a form, so land in the first field rather
    // than on the panel. Ones with no field keep the panel.
    const firstField = panelRef.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), select, textarea',
    );
    (firstField ?? panelRef.current)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [
        ...panel.querySelectorAll<HTMLElement>(
          'a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])',
        ),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      } else if (
        event.shiftKey &&
        (active === first || active === panel || !panel.contains(active))
      ) {
        event.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    // The page behind must not scroll under the sheet.
    //
    // <html>, not <body>. styles.css sets `html { overflow-x: clip }` and
    // `html, body { height: 100% }`, so the scrollport is the ROOT: hiding
    // body's overflow does not freeze the page, it CLIPS it to the viewport —
    // measured scrollY 322 -> 0 and scrollable height 1770 -> 0 -> 1770, so
    // every confirm dialog snapped the page behind it to the top and left it
    // there. Locking the element that actually scrolls freezes it in place.
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.documentElement.style.overflow = previousOverflow;
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, []);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        // mousedown, not click: a click that STARTS inside the panel and ends on
        // the backdrop (a drag while selecting text) would otherwise close it.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`modal${wide ? " modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="modal__head">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
          <IconButton label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        {children}
        {actions ? <div className="modal__actions">{actions}</div> : null}
      </div>
    </div>
  );
}

/** Confirmation dialog with a named, non-generic confirm action. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  confirming = false,
  destructive = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  /** Names what dismissing keeps — "Keep booking" next to "Cancel booking". */
  cancelLabel?: string;
  confirming?: boolean;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={confirming}>
            {cancelLabel}
          </Button>
          <Button
            // The danger variant is tonal, not the gradient: orange is the
            // colour of the thing you want people to do, so the one-primary
            // guardrail still holds while the error tone marks the irreversible
            // button.
            variant={destructive ? "danger" : "primary"}
            onClick={onConfirm}
            loading={confirming}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack muted">{body}</div>
    </Modal>
  );
}
